// ChatDrawer — right-side slide-in that reuses the full chat surface
// (ChatMessageList + ChatComposer + ChatHoverActions + ChatThreadPanel).
// Mirrors the chat page's shared root state (`channels`, `channelMembers`,
// `messagesByChannel`, `activeChannelId`, `activeThreadRootId`) so every
// bubble, composer affordance, hover action, and thread reply works
// identically to /chat. No parallel `_chatDrawer*` state.
//
// Data lifecycle:
//   - On open: if `root.channels` is empty (user hasn't visited /chat
//     this session) the drawer fetches channels + members itself.
//   - On active-channel change: loads the last 50 messages into
//     `messagesByChannel[id]` if absent, and binds a realtime
//     subscription scoped to that channel.
//   - Message sends + reactions route through the intranet's registered
//     functions (el.call('sendMessage', …)) which already target the
//     same shared state — nothing to wire.
//
// Event delegation: the /chat page installs document-level click /
// pointerdown / keydown handlers in its onCreate. When the drawer is
// opened from another route those handlers don't exist. We install a
// scoped subset from the drawer's onCreate — mention picking, thread
// open, tab / action buttons — guarded so we never double-install when
// the chat page IS mounted.

import { listChannels, listMessages, subscribeMessages } from './queries.js'
import { chatClient } from './client.js'
import { CHAT_CONFIG } from './config.js'

const { tables } = CHAT_CONFIG

// ─── shared-state loader ──────────────────────────────────────────────────

// Row shape used by ChatMessageList / ChatMessageRow — matches the
// intranet's `rowToMessage` exactly so the bubble renderer doesn't
// fork on where the data came from.
const _rowToMessage = (r) => ({
  id: r.id,
  channelId: r.channel_id,
  authorEmail: r.author_email,
  body: r.body,
  attachments: r.attachments || null,
  replyTo: r.reply_to,
  threadRoot: r.thread_root,
  threadCount: r.thread_count || 0,
  editedAt: r.edited_at,
  deleted: !!r.deleted,
  createdAt: r.created_at
})

const _rowToChannel = (r) => ({
  id: r.id,
  kind: r.kind,
  name: r.name,
  topic: r.topic,
  isPrivate: !!r.is_private,
  isArchived: !!r.is_archived,
  createdBy: r.created_by,
  createdAt: r.created_at,
  updatedAt: r.updated_at,
  _memberEmails: r._memberEmails || []
})

export const bootstrapChatState = async (root) => {
  if (!root) return
  // Editor doesn't auto-populate `root.currentUserEmail` the way the
  // intranet's auth flow does — pull it from the governance Supabase
  // session directly so bubble rendering (own-vs-other) + send paths
  // work everywhere the drawer mounts.
  if (!root.currentUserEmail) {
    const sb = chatClient()
    if (sb) {
      try {
        const { data } = await sb.auth.getUser()
        const email = data?.user?.email
        if (email) root.update({ currentUserEmail: email })
      } catch {}
    }
    if (!root.currentUserEmail) {
      root.update({
        _chatDrawerLoading: false,
        _chatDrawerError: 'Chat needs a workspace sign-in. Sign in via the intranet once to activate governance, then the drawer will work everywhere.'
      })
      return
    }
  }
  await _loadChannelsIntoRoot(root)
  if (root.activeChannelId) await _loadMessagesIntoRoot(root, root.activeChannelId)
}

// Switch the active channel and eagerly load messages — the drawer's
// onUpdate doesn't fire reliably on root-only state changes, so each
// click-site drives the load itself.
export const switchChannel = async (root, channelId) => {
  if (!root || !channelId) return
  root.update({
    activeChannelId: channelId,
    activeThreadRootId: null,
    _chatDrawerSwitcherOpen: false
  })
  await _loadMessagesIntoRoot(root, channelId)
}

const _loadChannelsIntoRoot = async (root) => {
  if ((root.channels || []).length) return
  // Editor runs with governance as an OPTIONAL extension — the Supabase
  // client for `governance` only exists after activateExtension() or
  // loginAll() has fanned out. When chat is opened in a context where
  // the session hasn't hydrated (e.g. editor logged in as a personal-org
  // user with no team-org claim), `chatClient()` is null and every query
  // would throw. Surface a readable state instead of crashing.
  const sb = chatClient()
  if (!sb) {
    root.update({
      _chatDrawerLoading: false,
      _chatDrawerError: 'Chat requires a governance session. Sign in with a workspace account to continue.'
    })
    return
  }
  root.update({ _chatDrawerLoading: true, _chatDrawerError: null })
  const [chRes, memRes] = await Promise.all([
    listChannels(),
    sb.from(tables.channelMembers).select('*')
  ])
  const channels = (chRes.data || []).map(_rowToChannel)
  const channelMembers = memRes.data || []
  const me = root.currentUserEmail
  const myIds = new Set(channelMembers.filter((m) => m.member_email === me).map((m) => m.channel_id))
  const active =
    root.activeChannelId && channels.some((c) => c.id === root.activeChannelId)
      ? root.activeChannelId
      : channels.find((c) => myIds.has(c.id))?.id || channels[0]?.id || null
  root.update({
    channels,
    channelMembers,
    activeChannelId: active,
    activeTab: root.activeTab || 'conversation',
    _chatDrawerLoading: false
  })
}

const _loadMessagesIntoRoot = async (root, channelId) => {
  if (!channelId) return
  const map = root.messagesByChannel || {}
  if ((map[channelId] || []).length) return
  const { data } = await listMessages({ channelId, limit: 50 })
  const msgs = (data || []).map(_rowToMessage).reverse()
  root.update({
    messagesByChannel: { ...(root.messagesByChannel || {}), [channelId]: msgs }
  })
}

// Channel name formatter — shared between the drawer header title,
// switcher trigger, and each row in the switcher popover.
const _channelLabel = (ch, root) => {
  if (!ch) return 'Chat'
  if (ch.kind === 'channel') return `#${ch.name || 'channel'}`
  const peopleByEmail = {}
  for (const p of root?.people || []) peopleByEmail[p.email] = p
  const others = (ch._memberEmails || []).filter((e) => e !== root?.currentUserEmail)
  const names = others.map((e) => peopleByEmail[e]?.name || e)
  if (ch.kind === 'dm') return names[0] || 'Direct message'
  return names.join(', ') || 'Group DM'
}

// ─── toggles (public) ─────────────────────────────────────────────────────

export const toggleChatDrawer = (root) => {
  if (!root) return
  root.update({ _chatDrawerOpen: !root._chatDrawerOpen })
}
export const openChatDrawer = (root, channelId = null) => {
  if (!root) return
  root.update({ _chatDrawerOpen: true, ...(channelId ? { activeChannelId: channelId } : {}) })
}
export const closeChatDrawer = (root) => root?.update({ _chatDrawerOpen: false })

// ─── minimal delegation (only when chat page is not mounted) ──────────────
// The /chat page installs a much richer handler set. When the drawer is
// invoked from another route we cover the subset the ChatComposer needs.

const _installDelegation = (root) => {
  if (typeof document === 'undefined') return () => {}
  if (document.__chatDrawerDelegationInstalled) return () => {}
  document.__chatDrawerDelegationInstalled = true

  const threadOpen = (ev) => {
    const node = ev.target?.closest?.('[data-thread-open]')
    if (!node) return
    const id = parseInt(node.getAttribute('data-thread-open'), 10)
    if (!isNaN(id)) {
      root.update({ activeThreadRootId: id })
      ev.stopPropagation()
    }
  }
  document.addEventListener('click', threadOpen, true)

  return () => {
    document.removeEventListener('click', threadOpen, true)
    document.__chatDrawerDelegationInstalled = false
  }
}

// ─── component ────────────────────────────────────────────────────────────

export const ChatDrawer = {
  hide: (el, s) => !s.root?._chatDrawerOpen,

  position: 'fixed',
  top: '0',
  right: '0',
  bottom: '0',
  width: 'H1',
  maxWidth: 'calc(100vw - A)',
  zIndex: '300',
  theme: 'document',
  background: 'document',
  borderLeft: '1px solid',
  borderColor: 'line',
  boxShadow: '-8px 0 32px var(--color-shadow)',
  flow: 'y',
  overflow: 'hidden',

  onCreate: (el, s) => {
    el._uninstall = _installDelegation(s.root)
  },

  onUpdate: (el, s) => {
    const root = s.root
    if (!root) return

    // Just opened → bootstrap channels if needed.
    const open = !!root._chatDrawerOpen
    if (open && !el._bootstrapped) {
      el._bootstrapped = true
      _loadChannelsIntoRoot(root).then(() => {
        if (root.activeChannelId) _loadMessagesIntoRoot(root, root.activeChannelId)
      })
    }
    if (!open) el._bootstrapped = false

    // Active channel changed → load messages + (re)bind realtime.
    const chId = root.activeChannelId
    if (el._subChannelId !== chId) {
      if (el._unsubRT) { try { el._unsubRT() } catch {} el._unsubRT = null }
      if (chId) {
        _loadMessagesIntoRoot(root, chId)
        el._unsubRT = subscribeMessages({
          channelId: chId,
          onInsert: (row) => {
            const msg = _rowToMessage(row)
            const map = root.messagesByChannel || {}
            const cur = map[msg.channelId] || []
            if (cur.some((m) => m.id === msg.id)) return
            root.update({
              messagesByChannel: { ...map, [msg.channelId]: [...cur, msg] }
            })
          }
        })
      }
      el._subChannelId = chId
    }
  },

  onRemove: (el) => {
    if (el._unsubRT) { try { el._unsubRT() } catch {} }
    if (el._uninstall) { try { el._uninstall() } catch {} }
  },

  // Header — title, channel select, close.
  Header: {
    flow: 'x',
    align: 'center space-between',
    padding: 'V Z',
    gap: 'V',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: 'line',
    flex: '0 0 auto',

    // Title doubles as the channel switcher trigger. Clicking the chip
    // opens a popover of all channels — matches common messenger UX
    // (Slack, Linear) where the active channel's name in the header IS
    // the switcher.
    ChannelSwitcher: {
      position: 'relative',
      flex: '1 1 auto',
      minWidth: '0',

      Trigger: {
        tag: 'button',
        type: 'button',
        flow: 'x',
        align: 'center left',
        gap: 'V',
        padding: 'V Z',
        margin: '0 0 0 -Z',
        round: 'Y',
        border: 'none',
        background: 'transparent',
        color: 'title',
        cursor: 'pointer',
        maxWidth: '100%',
        minWidth: '0',
        ':hover': { background: 'overlay' },
        transition: 'background 120ms ease',
        onClick: (e, el, s) => {
          if ((s.root?.channels?.length ?? 0) < 2) return
          s.root.update({ _chatDrawerSwitcherOpen: !s.root._chatDrawerSwitcherOpen })
        },
        Icon: { icon: 'chat', boxSize: 'Z1', color: 'caption', flex: '0 0 auto' },
        Label: {
          tag: 'span',
          fontSize: 'Z2',
          fontWeight: '700',
          letterSpacing: '-.005em',
          color: 'title',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: '0',
          text: (el, s) => {
            const root = s.root
            const ch = (root?.channels || []).find((c) => c.id === root?.activeChannelId)
            return ch ? _channelLabel(ch, root) : 'Chat'
          }
        },
        Caret: {
          extends: 'Icon',
          icon: 'chevronDown',
          boxSize: 'Z',
          color: 'caption',
          flex: '0 0 auto',
          hide: (el, s) => (s.root?.channels?.length ?? 0) < 2
        }
      },

      Popover: {
        hide: (el, s) => !s.root?._chatDrawerSwitcherOpen,
        position: 'absolute',
        top: 'calc(100% + 4px)',
        left: '0',
        zIndex: '10',
        width: 'H',
        maxWidth: 'calc(100vw - A)',
        maxHeight: 'F',
        overflow: 'auto',
        round: 'Z',
        background: 'overlayStrong',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'line',
        boxShadow: '0 8px 24px var(--color-shadow)',
        padding: 'V',
        flow: 'y',

        childExtends: 'ChatDrawerChannelRow',
        childrenAs: 'state',
        children: (el, s) => s.root?.channels || []
      }
    },

    CloseBtn: {
      extends: 'IconButton',
      title: 'Close',
      aria: { label: 'Close' },
      Icon: { icon: 'close' },
      onClick: (e, el, s) => closeChatDrawer(s.root)
    }
  },

  // Loading / empty screens — only while the first load is in flight.
  LoadingState: {
    hide: (el, s) => {
      const root = s.root
      if (!root?._chatDrawerLoading) return true
      return (root.channels || []).length > 0
    },
    flex: '1 1 auto',
    align: 'center center',
    flow: 'y',
    color: 'caption',
    fontSize: 'Z2',
    text: 'Loading…'
  },

  ErrorState: {
    hide: (el, s) => !s.root?._chatDrawerError,
    flex: '1 1 auto',
    align: 'center center',
    flow: 'y',
    gap: 'Z',
    color: 'caption',
    fontSize: 'Z2',
    padding: 'A',
    textAlign: 'center',
    text: (el, s) => s.root?._chatDrawerError || ''
  },

  EmptyState: {
    hide: (el, s) => {
      const root = s.root
      if (!root) return true
      if (root._chatDrawerLoading) return true
      if (root._chatDrawerError) return true
      return (root.channels || []).length > 0
    },
    flex: '1 1 auto',
    align: 'center center',
    flow: 'y',
    gap: 'Z',
    color: 'caption',
    fontSize: 'Z2',
    padding: 'A',
    text: 'No channels available.'
  },

  // Body — full chat surface. The MessageList + Composer + HoverActions
  // + ThreadPanel are the exact same components used on /chat, so bubble
  // + composer + hover menus + thread bubbles stay visually identical.
  Body: {
    hide: (el, s) => !(s.root?.activeChannelId),
    flex: '1 1 auto',
    flow: 'y',
    minHeight: '0',
    position: 'relative',
    overflow: 'hidden',

    // Full /chat surface — these components are registered via
    // `editor/packages/shared/components/chat/` and resolve in every
    // app whose sharedLibraries includes shared/context.js (intranet
    // AND editor canvas). Bubble style, markdown, reactions, hover
    // actions, mention-picker, and WYSIWYG composer all stay identical
    // to the /chat page.
    Conversation: {
      extends: 'ChatMessageList',
      flex: '1 1 0',
      padding: 'Z1 V V V',
      minHeight: '0',
      overflowX: 'hidden'
    },

    Composer: {
      extends: 'ChatComposer',
      flex: '0 0 auto',
      padding: '0 Z V Z',
      Toolbar: { width: '100%', margin: '0', gap: 'V' },
      Box: { width: '100%', margin: '0' }
    }
  },

  // Right-hand thread panel slides over the body when a bubble's
  // "Reply in thread" action is triggered. Same component as /chat.
  Thread: {
    extends: 'ChatThreadPanel',
    hide: (el, s) => !(s.root?._chatDrawerOpen && s.root?.activeThreadRootId),
    position: 'absolute',
    top: '0',
    right: '0',
    bottom: '0',
    width: '100%',
    zIndex: '10',
    background: 'document',
    borderLeftWidth: '1px',
    borderLeftStyle: 'solid',
    borderLeftColor: 'line'
  },

  // Shared viewport-fixed hover actions overlay — same instance pattern
  // as /chat's ChatMain so bubble-hover menus work inside the drawer.
  HoverActions: { extends: 'ChatHoverActions' }
}

// One row inside the drawer's channel-switcher popover. Highlights the
// active channel and writes `activeChannelId` on click.
export const ChatDrawerChannelRow = {
  tag: 'button',
  type: 'button',
  flow: 'x',
  align: 'center left',
  gap: 'Z',
  width: '100%',
  padding: 'V Z',
  round: 'V',
  border: 'none',
  cursor: 'pointer',
  fontSize: 'Z2',
  textAlign: 'left',
  color: 'title',
  background: (el, s) => (s.id === s.root?.activeChannelId ? 'overlay' : 'transparent'),
  ':hover': { background: 'overlay' },
  transition: 'background 100ms ease',
  onClick: (e, el, s) => switchChannel(s.root, s.id),
  Label: {
    tag: 'span',
    flex: '1 1 auto',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    text: (el, s) => _channelLabel(s, s.root)
  }
}

// Back-compat export — callers outside intranet may have imported this
// as the rendered bubble. Delegates to the real ChatMessageRow.
export const ChatDrawerMessage = { extends: 'ChatMessageRow' }
